import './styles/Faq.scss';

import React, { useEffect, useState } from 'react';

import ApplianceAccordion from '../components/appliance/ApplianceAccordion';
import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { TitleBanner } from '../components/Primitives';
import { graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        faqCms: allProductsJson(filter: { code: { eq: "drolet_faq" } }) {
            nodes {
                ...cmsDataFragment
                documents {
                    en_CA
                    en_US
                    fr_CA
                    caption {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        faqs: allProductsJson(
            filter: { category: { eq: "/Drolet/CMS/Faq/" } }
        ) {
            nodes {
                id
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    content {
                        valuesMarkdownRemark {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    faq_category {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Faq({ data, location }) {
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [accordionState, updateAccordionState] = useState('null');
    const { t } = useTranslation('pages');
    const faqCMS = data.faqCms.nodes[0];
    const faqAttributes = faqCMS?.attributes;

    const type = useLocation().pathname;

    const isCurrentType = (pageType: string): boolean => {
        return type.includes(pageType);
    };

    const isMobile = useMobileDetect().isMobile();
    const responsiveImages = data?.faqCms?.nodes?.[0]?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const metadata = {
        title: faqAttributes?.meta_title?.values[0][currentLocale],
        description: faqAttributes?.meta_description?.values[0][currentLocale],
    };

    useEffect(() => {
        if (location && typeof window !== 'undefined')
            window.scrollTo(0, location?.state?.scrollPosition);
    }, [location, location?.state]);

    useEffect(() => {
        if (location?.hash) {
            document
                .getElementById(location.hash.replace('#', ''))
                ?.scrollIntoView();
            updateAccordionState(location.hash.replace('#', ''));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // On veut ouvrir/scroll vers l'accordion seulement lorsque la page a loader

    const currentUrl = location?.pathname
        ?.split('/')
        .slice(3, location?.pathname?.split('/').length)
        .join('/');
    const currentCategory = currentUrl?.split('/').slice(1, 2).join('/');

    const faqList = data?.faqs?.nodes.filter((faq) => {
        const faqCategories = faq?.attributes?.faq_category?.values.map(
            (category) => category[currentLocale]
        );
        return faqCategories?.includes(currentCategory);
    });

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <TitleBanner
                title={faqCMS?.title?.[currentLocale]}
                imageData={isMobile ? mobileImage : desktopImage}
                className='faq-banner'
            />
            <FaqButtons />

            <div className='faq container' id='something'>
                {faqList?.map((faq) => {
                    const anchor = faq?.code?.replace('drolet_faq-', '');
                    return (
                        <ApplianceAccordion
                            key={faq?.id}
                            title={faq?.title[currentLocale]}
                            open={accordionState === anchor}
                            onClick={() => {
                                const scrollPosition =
                                    document.documentElement.scrollTop ||
                                    document.body.scrollTop;

                                accordionState === anchor
                                    ? updateAccordionState('null')
                                    : updateAccordionState(anchor);

                                const localeAnchor =
                                    accordionState === anchor ? '' : anchor;
                                navigateTranslated(
                                    currentUrl + `#${localeAnchor}`,
                                    scrollPosition
                                );
                            }}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: faq?.attributes?.content
                                        ?.valuesMarkdownRemark?.[0][
                                        currentLocale
                                    ],
                                }}
                            />
                        </ApplianceAccordion>
                    );
                })}
            </div>
        </Layout>
    );

    function FaqButtons() {
        return (
            <div className='faq-btns'>
                <Button
                    className={
                        isCurrentType(t('url:wood-stoves'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:wood-stoves')) &&
                            navigateTranslated(
                                '/faq/wood-stoves/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.wood-stoves')}
                />
                <Button
                    className={
                        isCurrentType(t('url:wood-inserts'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:wood-inserts')) &&
                            navigateTranslated(
                                '/faq/wood-inserts/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.wood-inserts')}
                />
                <Button
                    className={
                        isCurrentType(t('url:pellet-stoves'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:pellet-stoves')) &&
                            navigateTranslated(
                                '/faq/pellet-stoves/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.pellet-stoves')}
                />
                <Button
                    className={
                        isCurrentType(t('url:wood-furnaces'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:wood-furnaces')) &&
                            navigateTranslated(
                                '/faq/wood-furnaces/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.wood-furnaces')}
                />
                <Button
                    className={
                        isCurrentType(t('url:cook-stoves'))
                            ? 'btn selected'
                            : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:cook-stoves')) &&
                            navigateTranslated(
                                '/faq/cook-stoves/',
                                scrollPosition
                            );
                    }}
                    label={t('warranty.cook-stoves')}
                />
                <Button
                    className={
                        isCurrentType(t('url:others')) ? 'btn selected' : 'btn'
                    }
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        !isCurrentType(t('url:others')) &&
                            navigateTranslated('/faq/others/', scrollPosition);
                    }}
                    label={t('warranty.others')}
                />
            </div>
        );
    }
}
